import { useEffect, useState } from "react";
import { Button, Checkbox, Modal } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import type { BulkUnlinkPackageOptions } from "../shared/design-scope-create-mappers";

export type BulkUnlinkPackagesModalProps = {
  open: boolean;
  onCancel: () => void;
  onConfirm: (options: BulkUnlinkPackageOptions) => void | Promise<void>;
};

/**
 * Bulk unlink confirm: pick All Design Packages and/or All Bid Packages
 * before clearing links on the selected scope items.
 */
export default function BulkUnlinkPackagesModal(
  props: BulkUnlinkPackagesModalProps
) {
  const { open, onCancel, onConfirm } = props;
  const [unlinkDesignPackages, setUnlinkDesignPackages] = useState(true);
  const [unlinkBidPackages, setUnlinkBidPackages] = useState(true);
  const [inProgress, setInProgress] = useState(false);

  useEffect(() => {
    if (!open) return;
    setUnlinkDesignPackages(true);
    setUnlinkBidPackages(true);
    setInProgress(false);
  }, [open]);

  const canUnlink = unlinkDesignPackages || unlinkBidPackages;

  const handleConfirm = async () => {
    if (!canUnlink || inProgress) return;
    setInProgress(true);
    try {
      await onConfirm({ unlinkDesignPackages, unlinkBidPackages });
    } finally {
      setInProgress(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={() => {
        if (!inProgress) onCancel();
      }}
      destroyOnHidden
      closable={!inProgress}
      mask={{ closable: !inProgress }}
      data-testid="scope-bulk-unlink-modal"
      title={
        <div className="flex items-start gap-3 pr-4">
          <ExclamationCircleFilled
            className="mt-0.5 shrink-0 text-[22px]"
            style={{ color: "var(--ciq-warning-text)" }}
            aria-hidden
          />
          <span className="text-base font-semibold leading-snug">
            Select items to unlink.
          </span>
        </div>
      }
      footer={[
        <Button
          key="cancel"
          data-testid="scope-bulk-unlink-cancel"
          disabled={inProgress}
          onClick={onCancel}
        >
          Cancel
        </Button>,
        <Button
          key="unlink"
          type="primary"
          danger
          data-testid="scope-bulk-unlink-confirm"
          disabled={!canUnlink}
          loading={inProgress}
          onClick={handleConfirm}
        >
          Unlink
        </Button>
      ]}
    >
      <div className="flex flex-col gap-3 pl-9">
        <Checkbox
          checked={unlinkDesignPackages}
          disabled={inProgress}
          data-testid="scope-bulk-unlink-design-packages"
          onChange={(e) => setUnlinkDesignPackages(e.target.checked)}
        >
          All Design Packages
        </Checkbox>
        <Checkbox
          checked={unlinkBidPackages}
          disabled={inProgress}
          data-testid="scope-bulk-unlink-bid-packages"
          onChange={(e) => setUnlinkBidPackages(e.target.checked)}
        >
          All Bid Packages
        </Checkbox>
      </div>
    </Modal>
  );
}
